import { useCallback, useEffect, useRef, useState } from "react";
import { AiSparkle } from "@/components/common/AiSparkle";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { AssistantPanel } from "./AssistantPanel";
import { AskChips, AskForm, starterQuestions } from "./AskInputs";
import { useHomeChat } from "./useHomeChat";

const AskAiCard = ({ listing, onAsk, busy }) => (
  <div className="ai-glow mt-8 rounded-md" data-testid="ai-home-card">
    <div className="rounded-md bg-white p-5 sm:p-6">
      <div className="flex items-center gap-2">
        <AiSparkle className="h-6 w-6" />
        <p className="text-lg font-medium text-ink">Ask AI about this home</p>
      </div>
      <p className="mb-4 mt-1 text-[14px] text-neutral-600">How far to base, the closest Italian place, the nearest school, VA loan questions: answered in seconds.</p>
      <AskChips questions={starterQuestions(listing)} onAsk={onAsk} disabled={busy} testPrefix="ai-card" />
      <div className="mt-4"><AskForm onAsk={onAsk} busy={busy} testPrefix="ai-card" /></div>
    </div>
  </div>
);

/** Inline "Ask AI" card + floating button + side panel for one listing. */
export const HomeAssistant = ({ listing }) => {
  const { user, requireAccount } = useAuth();
  const [open, setOpen] = useState(false);
  const [allow, setAllow] = useState(null);
  const loaded = useRef(false);
  const chat = useHomeChat(listing.listing_key, (msg, retry) => requireAccount(msg, retry));
  const { load, send } = chat;

  const refreshAllowance = useCallback(() => {
    api.get("/ai/allowance").then((r) => setAllow(r.data ?? null)).catch(() => setAllow(null));
  }, []);
  useEffect(() => {
    if (open) refreshAllowance();
  }, [open, user, refreshAllowance]);

  const openPanel = async () => {
    setOpen(true);
    if (!loaded.current) {
      loaded.current = true;
      await load();
    }
  };
  const ask = async (q) => {
    await openPanel();
    await send(q);
    refreshAllowance();
  };
  const signUp = () => requireAccount("Create a free account to keep asking the AI home assistant and to save homes.", null);

  return (
    <>
      <AskAiCard listing={listing} onAsk={ask} busy={chat.busy} />
      <AssistantPanel listing={listing} open={open} onOpenChange={setOpen} chat={chat} allow={allow} onAsk={ask} onSignUp={signUp} />
    </>
  );
};
